/**
 * secureStorage.js - Secure wrapper for browser storage
 * Uses sessionStorage for auth tokens (cleared on tab close)
 * Provides fallback to localStorage for cross-tab persistence
 * Sensitive data is obfuscated using base64 encoding to prevent casual inspection
 * 
 * SENSITIVE keys: auth tokens, user data, impersonation tokens, biometric credentials
 * NON-SENSITIVE keys: UI preferences, PWA prompts, etc. (use localStorage directly)
 */

// Keys that contain sensitive/auth data - stored in BOTH sessionStorage + localStorage
const SENSITIVE_KEYS = [
  'token',
  'user',
  'parentData',
  'guruData',
  'original_manager_token',
  'original_manager_user',
  'original_superadmin_token',
  'original_superadmin_user',
  'biometricEnabled',
  'biometricCredentialId',
  'biometricUserId',
];

const isSensitive = (key) => SENSITIVE_KEYS.includes(key);

// Simple obfuscation for sensitive values (base64 + prefix marker)
const OBFUSCATION_PREFIX = 'ss_v1_';

const obfuscate = (value) => {
  try {
    return OBFUSCATION_PREFIX + btoa(unescape(encodeURIComponent(value)));
  } catch {
    return value;
  }
};

const deobfuscate = (value) => {
  try {
    if (value && value.startsWith(OBFUSCATION_PREFIX)) {
      return decodeURIComponent(escape(atob(value.slice(OBFUSCATION_PREFIX.length))));
    }
    return value; // Legacy unencoded value - return as-is for backward compat
  } catch {
    return value;
  }
};

/**
 * Store item - uses sessionStorage + localStorage for sensitive keys
 * Sensitive values are obfuscated before storage
 */
export const setItem = (key, value) => {
  try {
    const storedValue = isSensitive(key) ? obfuscate(value) : value;
    if (isSensitive(key)) {
      sessionStorage.setItem(key, storedValue);
    }
    localStorage.setItem(key, storedValue);
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') {
      console.error(`secureStorage: Failed to store '${key}':`, e);
    }
  }
};

/**
 * Get item - prefers sessionStorage for sensitive keys, falls back to localStorage
 * Sensitive values are deobfuscated on retrieval
 */
export const getItem = (key) => {
  try {
    let raw;
    if (isSensitive(key)) {
      raw = sessionStorage.getItem(key) || localStorage.getItem(key);
      return raw ? deobfuscate(raw) : null;
    }
    return localStorage.getItem(key);
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') {
      console.error(`secureStorage: Failed to read '${key}':`, e);
    }
    return null;
  }
};

/**
 * Remove item from both storages
 */
export const removeItem = (key) => {
  try {
    sessionStorage.removeItem(key);
    localStorage.removeItem(key);
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') {
      console.error(`secureStorage: Failed to remove '${key}':`, e);
    }
  }
};

// Convenience helpers for auth token
export const setAuthToken = (token) => setItem('token', token);
export const getAuthToken = () => getItem('token');
export const removeAuthToken = () => removeItem('token');

// Convenience helpers for user data
export const setUserData = (data) => {
  try {
    setItem('user', JSON.stringify(data));
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') {
      console.error('secureStorage: Failed to store user data:', e);
    }
  }
};

export const getUserData = () => {
  try {
    const data = getItem('user');
    return data ? JSON.parse(data) : null;
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') {
      console.error('secureStorage: Failed to parse user data:', e);
    }
    return null;
  }
};

// Clear all sensitive auth data
export const clearAuthStorage = () => {
  SENSITIVE_KEYS.forEach(key => {
    sessionStorage.removeItem(key);
    localStorage.removeItem(key);
  });
};

// Storage length & key iteration (for cleanup routines)
export const getLength = () => localStorage.length;
export const key = (index) => localStorage.key(index);

const secureStorage = {
  setItem,
  getItem,
  removeItem,
  setAuthToken,
  getAuthToken,
  removeAuthToken,
  setUserData,
  getUserData,
  clearAuthStorage,
  getLength,
  key,
};

export default secureStorage;
